import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError, filter, takeUntil, tap } from 'rxjs/operators';

import { AuthBackend } from '@libs/src/auth/auth.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { DialogConfirmPromptService } from '@libs/src/dialog-confirm-prompt/dialog-confirm-prompt.service';
import { SSOSignInParams } from '@libs/src/workos/workos.backend';
import {
  AUTH_TOKEN_KEY,
  LocalStorageService,
  REFRESH_TOKEN_KEY,
} from '@main-client/src/app/core/local-storage.service';
import { LocaleSettingsService } from '@main-client/src/app/core/locale-settings.service';
import { UserAnalyticsService } from '@main-client/src/app/user/user-analytics.service';

import { LoginResponse } from '@libs/src/interfaces/login-response.interface';
import { defaultLocaleCode } from '@libs/src/locale/locale.helper';
import { Account } from '@libs/src/models/account.model';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { getNewAccountRedirectParams } from '@main-client/src/app/auth/auth.helper';
import { hasSignedUpViaOfficeHours } from '@main-client/src/app/user/user.helpers';

import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import pickBy from 'lodash-es/pickBy';

const httpConflictStatus = 409;

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  providers: [DestroySubscriptionsService],
})
export class SignUpComponent implements OnInit {
  @Input() tenant: Tenant;
  form = this.fb.group({
    email: ['', Validators.compose([Validators.required, Validators.email])],
    first_name: ['', Validators.required],
    last_name: ['', Validators.required],
  });
  agreementFormControl = this.fb.control(false);
  isLoading = false;
  ssoSignInParams: SSOSignInParams = {};
  signUpBody: { inviteCode?: string; preRegistrationId?: string } = {};
  showSSOButton = false;
  ssoErrorMessage = '';
  ssoErrorStatus = '';

  constructor(
    @Inject(LOCALE_ID) private readonly appLocale: string,
    private readonly authBackend: AuthBackend,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly fb: UntypedFormBuilder,
    private readonly localStorageService: LocalStorageService,
    private readonly localeSettingsService: LocaleSettingsService,
    private readonly route: ActivatedRoute,
    private readonly userAnalyticsService: UserAnalyticsService,
    public confirmDialogPrompt: DialogConfirmPromptService,
    public router: Router,
  ) {}

  ngOnInit() {
    if (
      this.tenant?.integrations?.workos?.sso_login_enabled &&
      !this.tenant?.integrations?.workos?.sso_signup_enabled &&
      !this.tenant?.email_login_enabled
    ) {
      this.router.navigate(['/login'], {
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    this.route.queryParamMap
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (params) =>
            params.has('selectedLocale') &&
            params.get('selectedLocale') !== this.appLocale,
        ),
      )
      .subscribe((params) => {
        this.localeSettingsService.ensureBrowserLocale(
          this.tenant,
          params.get('selectedLocale'),
        );
      });
    const userSelectedLocale =
      this.route.snapshot.queryParamMap.get('selectedLocale');
    this.ssoErrorMessage =
      this.route.snapshot.queryParamMap.get('ssoErrorMessage');
    this.ssoErrorStatus =
      this.route.snapshot.queryParamMap.get('ssoErrorStatus');
    this.showSSOButton = this.tenant.integrations?.workos?.sso_signup_enabled;
    const routeSnapshotData = this.route.snapshot.data;
    const inviteCode = get(routeSnapshotData, 'invitation.inviteCode');
    if (inviteCode) {
      this.signUpBody.inviteCode = inviteCode;
    }
    const email =
      this.route.snapshot.queryParamMap.get('email') ||
      routeSnapshotData?.programMembership?.email;
    const firstName = this.route.snapshot.queryParamMap.get('firstName');
    const lastName = this.route.snapshot.queryParamMap.get('lastName');
    if (email || firstName || lastName) {
      this.form.patchValue(
        pickBy({
          email,
          first_name: firstName,
          last_name: lastName,
        }),
      );
    }
    if (email && firstName && lastName) {
      this.agreementFormControl.patchValue(true);
      return this.submitForm(this.form);
    }
    const preRegistrationAccount = get(
      routeSnapshotData,
      'preRegistration.account',
      {},
    ) as Account;
    if (!isEmpty(preRegistrationAccount)) {
      if (
        preRegistrationAccount.locale_config?.locale_selected !==
          defaultLocaleCode &&
        !userSelectedLocale
      ) {
        this.localeSettingsService.ensureBrowserLocale(
          this.tenant,
          preRegistrationAccount.locale_config?.locale_selected,
        );
      }
      this.signUpBody.preRegistrationId = preRegistrationAccount._id;
      this.form.patchValue({
        email: preRegistrationAccount.email,
        first_name: preRegistrationAccount.first_name,
        last_name: preRegistrationAccount.last_name,
      });
    }
    const {
      redirectQueryParams,
      redirectState,
      signup_method,
      signup_method_details,
    } = this.getSignUpConfig();
    const destination = this.getRedirectUrl(redirectState, redirectQueryParams);
    this.ssoSignInParams = pickBy({
      destination,
      signup_method,
      signup_method_details,
      ...this.signUpBody,
    });
  }

  private getRedirectUrl(redirectState?: string, redirectQueryParams?: any) {
    if (!redirectState) {
      return this.route.snapshot.queryParamMap.get('redirectUrl');
    }
    if (isEmpty(redirectQueryParams)) {
      return redirectState;
    }
    const searchParams = new URLSearchParams(redirectQueryParams);
    return `${redirectState}?${searchParams.toString()}`;
  }

  submitForm(form: UntypedFormGroup) {
    if (this.isLoading) {
      return;
    }
    this.form.disable();
    this.agreementFormControl.disable();
    this.isLoading = true;
    const formValue = form.value;
    const {
      redirectQueryParams,
      redirectState,
      signup_method,
      signup_method_details,
      token,
    } = this.getSignUpConfig();
    const signUpBody = {
      ...this.signUpBody,
      ...formValue,
      locale_config: { locale_selected: this.appLocale },
      signup_method,
      signup_method_details,
    };
    this.authBackend
      .signUp(signUpBody, {
        ...(token && { token }),
      })
      .pipe(
        tap((response: LoginResponse) => {
          this.localStorageService.setItem(AUTH_TOKEN_KEY, response.token);
          this.localStorageService.setItem(
            REFRESH_TOKEN_KEY,
            response.refresh_token,
          );
          this.userAnalyticsService.sendAnalyticsIdentify(response.profile);
          if (response.isNewAccount) {
            this.userAnalyticsService.setAnalyticsAlias(response.profile);
          }
        }),
        catchError((responseError: any) => {
          this.isLoading = false;
          this.form.enable();
          this.agreementFormControl.enable();
          if (httpConflictStatus === responseError.status) {
            this.attemptLoginWithEmail();
          } else {
            this.form.setErrors({
              server: responseError.error.message,
            });
          }
          return EMPTY;
        }),
      )
      .subscribe((response: LoginResponse) => {
        if (redirectState) {
          return this.router.navigate([redirectState], {
            queryParams: redirectQueryParams,
          });
        }
        if (
          response.isNewAccount &&
          hasSignedUpViaOfficeHours(response.profile)
        ) {
          return this.router.navigate(['/verify-email-prompt']);
        }
        if (response.isNewAccount) {
          const { path, queryParams } = getNewAccountRedirectParams(
            response.profile,
            this.route.snapshot.data.preRegistration?.decodedToken?.groupId,
          );
          return this.router.navigate(path, { queryParams });
        }
        return this.router.navigate(['/home']);
      });
  }

  getSignUpConfig() {
    const programMembership = this.route.snapshot.data?.programMembership;
    const isProgramSignUp = !isEmpty(programMembership);
    if (isProgramSignUp) {
      return {
        redirectQueryParams: {
          preregistrationGroupId: programMembership.group._id,
        },
        redirectState: `/get-started/hub-segments`,
        signup_method: 'development_program',
        signup_method_details: `${programMembership.program?._id}`,
        token: this.route.snapshot.queryParamMap.get('programToken'),
      };
    }
    const eventSlug = this.route.snapshot.queryParamMap.get('event');
    const eventId = this.route.snapshot.queryParamMap.get('eventId');
    const isEventSignUp = eventSlug && eventId;
    if (isEventSignUp) {
      return {
        signup_method: 'office_hours',
        signup_method_details: `${eventId}-${eventSlug}`,
      };
    }
    return {
      token: this.route.snapshot.data.preRegistration?.preRegistrationToken,
    };
  }

  attemptLoginWithEmail() {
    const email = this.form.get('email').value;
    this.confirmDialogPrompt
      .openDialog({
        cancel: 'Try another email',
        color: 'primary',
        confirm: 'Log in',
        content: $localize`:@@confirmPromptDialog.content.loginPrompt:We've found an account for ${email}. Did you mean to log in instead?`,
        title: 'You already have an account',
      })
      .afterClosed()
      .subscribe((response) => {
        if (!response) {
          return;
        }

        const inviteCode = get(this.route, 'snapshot.queryParams.inviteCode');
        const redirectUrlAfterLogin = inviteCode
          ? `/invitation/${inviteCode}`
          : '';
        this.router.navigate(['/login'], {
          queryParams: {
            email,
            autoSubmit: true,
            ...(redirectUrlAfterLogin && {
              redirectUrl: redirectUrlAfterLogin,
            }),
          },
          queryParamsHandling: 'merge',
        });
      });
  }
}
